import clsx from 'clsx'
import compact from 'lodash/compact'
import isNumber from 'lodash/isNumber'
import map from 'lodash/map'
import React, { FC, useEffect, useRef, useState } from 'react'

import SvgAlert from '@Assets/SVG/Alert'
import SvgInfo from '@Assets/SVG/Info'
import SvgInfoBox from '@Assets/SVG/InfoBox'
import SvgInfoFilled from '@Assets/SVG/InfoFilled'
import SvgThumbsUp from '@Assets/SVG/ThumbsUp'
import { Button } from '@CoreComponents/Buttons/Button'
import { ShippingLogo } from '@Domains/Checkout/Steps/Delivery/Components/DeliveryLogo'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { CheckoutCartV2ShippingMethod, CheckoutV2ServiceLocation } from '@Pages/onestepcheckout'
import { NotEmptyValidationExternalCustomerID } from '@Shared/Components/Forms/Validations/NotEmptyValidation'
import { RoundCheckbox } from '@Shared/Components/Inputs/Checkbox/RoundCheckbox'
import { TextInput, TextInputActionProps } from '@Shared/Components/Inputs/TextInput/TextInput'

import styles from './DeliveryOption.module.scss'

interface DeliveryOptionV2Props {
  autoSelectedServiceLocation?: boolean
  checkError?: boolean
  className?: string
  deliveryMethod: CheckoutCartV2ShippingMethod
  disabled?: boolean
  externalCustomerID: string
  header?: string
  hide?: boolean
  isSelected: boolean
  loadingDropPoints?: boolean
  onContinue?: () => void
  onSelect: () => void
  openDropPointDialog?: () => void
  openInfoDialog?: () => void
  selectedServiceLocation?: CheckoutV2ServiceLocation
  setExternalCustomerID: (externalCustomerID: string) => void

}

export const DeliveryOptionV2: FC<DeliveryOptionV2Props> = ({
  autoSelectedServiceLocation,
  checkError,
  className,
  deliveryMethod,
  disabled,
  externalCustomerID,
  header,
  hide,
  isSelected,
  loadingDropPoints,
  onContinue,
  onSelect,
  openDropPointDialog,
  openInfoDialog,
  selectedServiceLocation,
  setExternalCustomerID,
}) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const externalCustomerIDRef = useRef<TextInputActionProps | null>(null)
  const ref = useRef<HTMLDivElement | null>(null)
  const [errorMessage, setErrorMessage] = useState<null | string>(null)
  const isFree = deliveryMethod.price?.discount ? deliveryMethod.price?.discount.value === 0 : deliveryMethod.price?.value === 0
  const discountPrice = deliveryMethod.price?.discount ? deliveryMethod.price?.discount.format : null
  const price = deliveryMethod.price?.format
  const distance = selectedServiceLocation && 'distance' in selectedServiceLocation ? selectedServiceLocation?.distance : undefined
  const selectedServiceLocationInformation = !selectedServiceLocation
    ? null
    : compact([
      selectedServiceLocation?.name,
      selectedServiceLocation?.streetName,
      `${selectedServiceLocation?.postalCode}, ${selectedServiceLocation?.city}`,
    ])

  const showExternalCustomerID = isSelected && deliveryMethod.isExternalCustomerIDRequired

  useEffect(() => {
    if (checkError) {
      if (isSelected && deliveryMethod && deliveryMethod.isServiceLocation && !selectedServiceLocation) {
        setErrorMessage(t('checkout:COF_YOU_NEED_TO_SELECT_A_PICKUP_POINT'))
        ref.current?.scrollIntoView({ behavior: 'smooth' })
      }
      if (externalCustomerIDRef.current) {
        externalCustomerIDRef.current?.touch()
        externalCustomerIDRef.current?.validate()
      }
    } else {
      setErrorMessage(null)
    }
  }, [checkError, deliveryMethod, isSelected, selectedServiceLocation, t])
  if (hide) {
    return null
  }

  const findLocationButtonTheme = () => {
    if (cofDesignV1 && !selectedServiceLocationInformation) {
      return 'black'
    }
    if (cofDesignV1) {
      return 'label'
    }
    return 'primary'
  }

  return (
    <div
      className={clsx(
        styles.deliveryOption,
        disabled && styles.disabled,
        header && styles.withHeader,
        cofDesignV1 && styles.cofDesignV1,
        isSelected && styles.isSelected,
        className,
      )}
      onClick={() => {
        if (!disabled) {
          onSelect()
        }
      }}
      ref={ref}
    >
      {header && <h3 className={styles.header}>{header}</h3>}
      <div className={styles.checkbox}>
        <RoundCheckbox ariaLabelledby={`delivery-option-${deliveryMethod.id}`} checked={isSelected} />
      </div>
      <div className={styles.logo}>
        <ShippingLogo deliveryType={deliveryMethod.icon || ''} url={deliveryMethod.logo?.url} />
      </div>
      <div className={styles.content}>
        {cofDesignV1 && !disabled && deliveryMethod.isRecommended && (
          <span className={styles.recommended}>{t('checkout:COF_WE_RECOMMEND_THIS_SERVICE')}</span>
        )}
        <span>
          <p className={styles.name} id={`delivery-option-${deliveryMethod.id}`}>{deliveryMethod.title}</p>
          {deliveryMethod.description && (
            // eslint-disable-next-line jsx-a11y/control-has-associated-label
            <button
              aria-label={t('checkout:COF_DELIVERY_OPTION_INFO')}
              className={styles.infoIcon}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (openInfoDialog) {
                  openInfoDialog()
                }
              }}
              type="button"
            >
              {!cofDesignV1 ? <SvgInfoFilled /> : <SvgInfo />}
            </button>
          )}
        </span>
        {!disabled && <p className={styles.deliveryTime}>{`${t('checkout:COF_DELIVERY_TIME')}: ${deliveryMethod.deliveryTimeText}`}</p>}
        {!cofDesignV1 && !disabled && deliveryMethod.isRecommended && (
          <span className={styles.recommended}>
            <SvgThumbsUp />
            {t('checkout:COF_WE_RECOMMEND_THIS_SERVICE')}
          </span>
        )}
        {disabled && <b className={styles.unavailable}>{t('checkout:COF_NOT_AVAILABLE')}</b>}
        {disabled && <p className={styles.unavailable}>{t('checkout:SHIPPING_UNAVAILABLE', '')}</p>}

        {deliveryMethod.isServiceLocation && (
          <div className={isSelected && cofDesignV1 ? styles.serviceLocationCofDesignV1 : ''}>
            {deliveryMethod.isServiceLocation && !selectedServiceLocation && (
              <span className={clsx(styles.choosePickupText, !isSelected && styles.unclear)}>
                {!cofDesignV1 && (<SvgInfoBox />)}
                {t('checkout:COF_CHOOSE_OWN_PICKUP_LOCATION')}
              </span>
            )}

            {isSelected && errorMessage && (
              <span className={styles.error}>
                <SvgAlert />
                <p>{errorMessage}</p>
              </span>
            )}
            <div className={styles.pickupInformation}>
              {!errorMessage && isSelected && isNumber(distance) && selectedServiceLocationInformation && (
                <div className={styles.dropPointInformation}>
                  <strong className={styles.pickedLocation}>
                    {autoSelectedServiceLocation
                      ? t('checkout:COF_AUTO_SELECTED_PICKUP_LOCATION')
                      : t('checkout:COF_SELECTED_PICKUP_LOCATION')}
                  </strong>
                  <div className={styles.info}>{map(selectedServiceLocationInformation, info => <p key={`deliveryOptionInfo_${info}`}>{info}</p>)}</div>
                  {isNumber(selectedServiceLocation?.distance) && (
                    <div className={styles.distance}>
                      {selectedServiceLocation!.distance === 0 ? 0 : (selectedServiceLocation!.distance / 1000).toFixed(2)}
                      {t('checkout:KILOMETER_SHORT')}
                    </div>
                  )}
                </div>
              )}
              {isSelected && (
                <Button
                  className={clsx(!cofDesignV1 && styles.findLocation)}
                  disabled={loadingDropPoints}
                  fillWidth
                  innerSpacing={selectedServiceLocationInformation ? 'small' : 'medium'}
                  loading={loadingDropPoints}
                  onClick={openDropPointDialog}
                  theme={findLocationButtonTheme()}
                >
                  {t('checkout:COF_FIND_LOCATION')}
                </Button>
              )}
              {showExternalCustomerID
                && (
                  <div className={styles.externalCustomerID}>
                    <TextInput
                      actionRef={externalCustomerIDRef}
                      label="DHL Postnummer *"
                      onChange={v => setExternalCustomerID(v)}
                      validations={[NotEmptyValidationExternalCustomerID]}
                      value={externalCustomerID}
                    />
                  </div>
                )}
              {((isSelected && isNumber(distance) && selectedServiceLocationInformation) || showExternalCustomerID)
                && (
                  <Button
                    className={cofDesignV1 ? '' : clsx(styles.continueButton, showExternalCustomerID && styles.submitExternalCustomerID, styles.newDesign)}
                    fillWidth={cofDesignV1}
                    innerSpacing="medium"
                    onClick={() => onContinue && onContinue()}
                    theme={cofDesignV1 ? 'black' : 'primary'}
                  >
                    {t('checkout:COF_CONFIRM_LOCATION')}
                  </Button>
                )}
            </div>
          </div>
        )}
      </div>
      <div className={styles.price}>
        <p className={clsx((isFree || discountPrice) && styles.strikeThrough)}>{price}</p>
        {isFree && <p className={styles.discountedPrice}>{t('header:FREE_LABEL')}</p>}
        {!isFree && discountPrice && <p className={styles.discountedPrice}>{discountPrice}</p>}
      </div>
    </div>
  )
}
